import { forwardRef } from "react";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";

/**
 * ShareVerseCard
 * ---------------
 * Off-screen (or visible) render target for exporting a beautiful,
 * share-ready parchment image of a single verse. Rendered into the
 * DOM and then converted to PNG via html-to-image.
 *
 * The card is sized 1080x1350 (Instagram portrait) for social share.
 */
const ShareVerseCard = forwardRef(function ShareVerseCard(
  { verseHe, verseEn, bookHe, bookEn, chapter, verse, theme = "parchment" },
  ref
) {
  const palette = getPalette(theme);

  return (
    <div
      ref={ref}
      data-testid="share-verse-card"
      dir="rtl"
      style={{
        width: 1080,
        height: 1350,
        position: "relative",
        overflow: "hidden",
        padding: 96,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        fontFamily:
          '"Frank Ruhl Libre", "David Libre", "Noto Serif Hebrew", serif',
        background: palette.bg,
        color: palette.text,
      }}
    >
      {/* parchment / texture layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: palette.bgTexture,
          pointerEvents: "none",
        }}
      />
      {/* gold frame */}
      <div
        style={{
          position: "absolute",
          inset: 48,
          border: `2px solid ${palette.gold}`,
          boxShadow: `inset 0 0 0 10px transparent, inset 0 0 0 12px ${palette.goldSoft}`,
          pointerEvents: "none",
        }}
      />
      {/* corner ornaments */}
      {[
        { top: 32, left: 32 },
        { top: 32, right: 32 },
        { bottom: 32, left: 32 },
        { bottom: 32, right: 32 },
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            ...pos,
            color: palette.gold,
            fontSize: 32,
            lineHeight: 1,
            fontFamily: "serif",
          }}
        >
          ✦
        </div>
      ))}

      {/* Top header */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          marginTop: 32,
        }}
      >
        <div
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            letterSpacing: 10,
            textTransform: "uppercase",
            fontSize: 20,
            color: palette.gold,
          }}
        >
          Torahya · תוריא
        </div>
        <div
          style={{
            marginTop: 18,
            height: 1,
            width: 140,
            marginLeft: "auto",
            marginRight: "auto",
            background: `linear-gradient(90deg, transparent, ${palette.gold}, transparent)`,
          }}
        />
      </div>

      {/* Main verse */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          padding: "0 16px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 48,
        }}
      >
        <p
          style={{
            fontSize: Math.min(78, Math.max(38, 1600 / Math.max(40, (verseHe || "").length))),
            lineHeight: 1.65,
            margin: 0,
            fontWeight: 500,
            color: palette.text,
          }}
        >
          {verseHe}
        </p>
        {verseEn && (
          <p
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 26,
              lineHeight: 1.55,
              margin: 0,
              color: palette.muted,
              direction: "ltr",
            }}
          >
            “{verseEn}”
          </p>
        )}
      </div>

      {/* Citation */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontSize: 16,
            letterSpacing: 12,
            color: palette.gold,
            marginBottom: 18,
          }}
        >
          ✦ ❖ ✦
        </div>
        <div
          style={{
            fontSize: 34,
            color: palette.gold,
            fontWeight: 600,
          }}
        >
          {bookHe} · פרק {numberToHebrewLetter(chapter)} · פסוק {numberToHebrewLetter(verse)}
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 18,
            letterSpacing: 6,
            color: palette.muted,
          }}
        >
          {bookEn} {chapter}:{verse}
        </div>
      </div>
    </div>
  );
});

function getPalette(theme) {
  if (theme === "dark") {
    return {
      bg: "hsl(219, 40%, 11%)",
      bgTexture:
        "radial-gradient(ellipse at 15% 10%, hsl(219, 45%, 16%) 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, hsl(219, 40%, 9%) 0%, transparent 55%)",
      text: "hsl(40, 42%, 85%)",
      muted: "hsl(40, 24%, 68%)",
      gold: "hsl(41, 58%, 62%)",
      goldSoft: "hsl(41, 58%, 62% / 0.25)",
    };
  }
  if (theme === "white") {
    return {
      bg: "#ffffff",
      bgTexture: "none",
      text: "hsl(222, 47%, 11%)",
      muted: "hsl(215, 16%, 47%)",
      gold: "hsl(36, 75%, 42%)",
      goldSoft: "hsl(36, 75%, 42% / 0.2)",
    };
  }
  // parchment (default)
  return {
    bg: "hsl(40, 45%, 93%)",
    bgTexture:
      "radial-gradient(ellipse at 10% 0%, hsl(40, 40%, 86%) 0%, transparent 60%), radial-gradient(ellipse at 90% 100%, hsl(40, 35%, 85%) 0%, transparent 60%)",
    text: "hsl(216, 55%, 22%)",
    muted: "hsl(216, 25%, 42%)",
    gold: "hsl(36, 75%, 38%)",
    goldSoft: "hsl(36, 75%, 38% / 0.25)",
  };
}

export default ShareVerseCard;
